import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import ImageUploader from './ImageUploader';

// Format number to currency string (e.g., 1700300 -> $1,700,300 or $5,600/month for rentals)
const formatPrice = (value, listingType = null) => {
  if (!value) return '';
  // Remove all non-numeric characters (including /month)
  const numericValue = value.toString().replace(/[^0-9]/g, '');
  if (!numericValue) return '';
  // Convert to number and format with commas
  const number = parseInt(numericValue, 10);
  const formatted = `$${number.toLocaleString('en-US')}`;
  // Append /month for rentals
  if (listingType === 'rentals' || listingType === 'past-rentals') {
    return `${formatted}/month`;
  }
  return formatted;
};

// Parse formatted price back to numeric string for storage
const parsePrice = (value) => {
  if (!value) return '';
  // Remove $, commas, and /month, return just the number
  return value.toString().replace(/[$,]/g, '').replace(/\/month/gi, '');
};

const getValidationSchema = (listingType) => {
  const baseSchema = {
    price: Yup.string()
      .notRequired()
      .test('price-format', 'Price should be a valid number (e.g., "$1,200,000" or "1200000")', (value) => {
        if (!value) return true; // optional field
        // Remove $, commas, and /month, then check if it's a valid number
        const numericValue = value.replace(/[$,]/g, '').replace(/\/month/gi, '');
        return !isNaN(numericValue) && parseFloat(numericValue) > 0;
      }),
    address: Yup.string()
      .notRequired()
      .test('address-length', 'Address must be at least 5 characters if provided', (value) => {
        if (!value) return true; // optional field
        return value.length >= 5;
      }),
    url: Yup.string()
      .notRequired()
      .test('url-format', 'Must be a valid URL (e.g., https://example.com)', (value) => {
        if (!value) return true; // optional field
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      }),
  };

  if (listingType === 'short-term-rentals') {
    return Yup.object({
      title: Yup.string()
        .notRequired()
        .test('title-length', 'Title must be at least 3 characters if provided', (value) => {
          if (!value) return true; // optional field
          return value.length >= 3;
        }),
      subtitle: Yup.string()
        .notRequired()
        .test('subtitle-length', 'Subtitle must be at least 3 characters if provided', (value) => {
          if (!value) return true; // optional field
          return value.length >= 3;
        }),
      rating: Yup.number()
        .notRequired()
        .min(0, 'Rating must be 0 or greater')
        .max(5, 'Rating must be 5 or less')
        .test('decimal-places', 'Rating can have up to 2 decimal places', (value) => {
          if (value === undefined || value === null || value === '') return true;
          const decimalPlaces = (value.toString().split('.')[1] || '').length;
          return decimalPlaces <= 2;
        }),
      reviewsCount: Yup.number()
        .notRequired()
        .min(0, 'Reviews count must be 0 or greater')
        .integer('Reviews count must be a whole number'),
      beds: Yup.number()
        .notRequired()
        .min(1, 'Beds must be at least 1')
        .integer('Beds must be a whole number')
        .test('beds-validation', 'Beds must be at least 1', (value) => {
          if (value === undefined || value === null || value === '') return true;
          return Number.isInteger(value) && value >= 1;
        }),
      baths: Yup.number()
        .notRequired()
        .positive('Baths must be a positive number')
        .test('baths-validation', 'Baths must be a positive number', (value) => {
          if (value === undefined || value === null || value === '') return true;
          return value > 0;
        }),
      guests: Yup.number()
        .notRequired()
        .integer('Guests must be a whole number')
        .test('guests-validation', 'Guests must be at least 1 if provided', (value) => {
          if (value === undefined || value === null || value === '') return true;
          return Number.isInteger(value) && value >= 1;
        }),
      location: Yup.string()
        .notRequired()
        .test('location-length', 'Location must be at least 3 characters if provided', (value) => {
          if (!value) return true; // optional field
          return value.length >= 3;
        }),
      status: Yup.string()
        .notRequired()
        .oneOf(['Active', 'Pending', 'Sold', 'Rented'], 'Status must be Active, Pending, Sold, or Rented'),
      price: Yup.string()
        .notRequired()
        .test('price-format', 'Price should be a valid number (e.g., "$1,200,000" or "1200000")', (value) => {
          if (!value) return true; // optional field
          // Remove $, commas, and /month, then check if it's a valid number
          const numericValue = value.replace(/[$,]/g, '').replace(/\/month/gi, '');
          return !isNaN(numericValue) && parseFloat(numericValue) > 0;
        }),
      address: Yup.string().notRequired(), // Address is optional for short-term rentals
      url: baseSchema.url,
    });
  }

  return Yup.object({
    beds: Yup.number()
      .notRequired()
      .integer('Beds must be a whole number')
      .test('beds-validation', 'Beds must be at least 1', (value) => {
        if (value === undefined || value === null || value === '') return true;
        return Number.isInteger(value) && value >= 1;
      }),
    baths: Yup.number()
      .notRequired()
      .positive('Baths must be a positive number')
      .test('baths-validation', 'Baths must be a positive number', (value) => {
        if (value === undefined || value === null || value === '') return true;
        return value > 0;
      }),
    sqft: Yup.number()
      .notRequired()
      .integer('Square feet must be a whole number')
      .test('sqft-validation', 'Square feet must be at least 1', (value) => {
        if (value === undefined || value === null || value === '') return true;
        return Number.isInteger(value) && value >= 1;
      }),
    acres: Yup.string()
      .test('acres-format', 'Acres must be a valid number (e.g., "2.5" or "2")', (value) => {
        if (!value) return true; // optional field
        return !isNaN(value) && parseFloat(value) >= 0;
      }),
    status: Yup.string()
      .notRequired()
      .oneOf(['Active', 'Pending', 'Sold', 'Rented'], 'Status must be Active, Pending, Sold, or Rented'),
    ...baseSchema,
  });
};

const getInitialValues = (listingType, listing = null) => {
  if (listing) {
    // Format price and HOA if they exist and aren't already formatted
    // If price doesn't have $, format it (formatPrice will add /month for rentals)
    const formattedPrice = listing.price && !listing.price.toString().includes('$') 
      ? formatPrice(listing.price, listingType) 
      : listing.price || '';
    const formattedHOA = listing.hoa && !listing.hoa.toString().includes('$')
      ? formatPrice(listing.hoa, listingType)
      : listing.hoa || '';
    
    return {
      ...listing,
      price: formattedPrice,
      hoa: formattedHOA,
      images: listing.images || [],
      image: listing.image || null,
    };
  }

  // Set default status based on listing type
  let defaultStatus = 'Active';
  if (listingType === 'past-rentals') {
    defaultStatus = 'Rented';
  } else if (listingType === 'past-sales') {
    defaultStatus = 'Sold';
  } else if (listingType === 'active-sales' || listingType === 'rentals' || listingType === 'short-term-rentals') {
    defaultStatus = 'Active';
  }

  const base = {
    price: '',
    beds: '',
    baths: '',
    sqft: '',
    address: '',
    status: defaultStatus,
    url: '',
    images: [],
    image: null,
  };

  if (listingType === 'short-term-rentals') {
    return {
      ...base,
      title: '',
      subtitle: '',
      rating: '',
      reviewsCount: '',
      bedrooms: '',
      guests: '',
      location: '',
    };
  }

  if (listingType === 'past-sales' || listingType === 'past-rentals') {
    return {
      ...base,
      image: null,
      images: [],
    };
  }

  return base;
};

const ListingForm = ({ open, onClose, onSubmit, listingType, listing = null }) => {
  const isEdit = !!listing;
  const isShortTerm = listingType === 'short-term-rentals';
  const isPast = listingType === 'past-sales' || listingType === 'past-rentals';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }
      }}
    >
      <Formik
        initialValues={getInitialValues(listingType, listing)}
        validationSchema={getValidationSchema(listingType)}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            // Parse price and HOA from formatted strings to numeric strings
            const parsedPrice = values.price ? parsePrice(values.price) : undefined;
            const parsedHOA = values.hoa ? parsePrice(values.hoa) : undefined;
            
            // Convert string numbers to actual numbers, handling empty values
            // Only convert if value exists and is not empty string
            const convertToNumber = (val) => {
              if (val === '' || val === null || val === undefined) return undefined;
              const num = Number(val);
              return isNaN(num) ? undefined : num;
            };

            const processedValues = {
              ...values,
              price: parsedPrice, // Store as unformatted number string or undefined
              hoa: parsedHOA, // Store as unformatted number string or undefined
              beds: convertToNumber(values.beds),
              baths: convertToNumber(values.baths),
              sqft: convertToNumber(values.sqft),
              rating: convertToNumber(values.rating),
              reviewsCount: convertToNumber(values.reviewsCount),
              guests: convertToNumber(values.guests),
              bedrooms: convertToNumber(values.bedrooms),
            };

            // For past listings, use image instead of images
            if (isPast) {
              processedValues.image = processedValues.images?.[0] || processedValues.image || null;
              delete processedValues.images;
              // Remove image field if it's null (optional field)
              if (processedValues.image === null || processedValues.image === undefined) {
                delete processedValues.image;
              }
            } else {
              // Ensure images array is included for non-past listings
              if (!processedValues.images || processedValues.images.length === 0) {
                processedValues.images = [];
              }
              delete processedValues.image;
            }

            // Remove empty string, undefined, and null values (all fields are now optional)
            Object.keys(processedValues).forEach(key => {
              if (processedValues[key] === '' || processedValues[key] === undefined || processedValues[key] === null) {
                // Keep images array structure, delete everything else that's empty
                if (key !== 'images' && key !== 'image') {
                  delete processedValues[key];
                }
              }
            });
            
            // Handle status - server will set default if not provided
            if (!processedValues.status || processedValues.status === '') {
              delete processedValues.status;
            }

            // Debug: Log what's being submitted
            console.log('Submitting listing with values:', {
              ...processedValues,
              images: processedValues.images,
              image: processedValues.image,
            });

            await onSubmit(processedValues);
            onClose();
          } catch (error) {
            console.error('Error submitting form:', error);
            const errorMessage = error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Error saving listing. Please try again.';
            alert(errorMessage);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
          // Handle price field formatting
          const handlePriceChange = (e) => {
            const rawValue = e.target.value;
            // Allow typing, but format on blur
            setFieldValue('price', rawValue);
          };

          const handlePriceBlur = (e) => {
            // Only format with currency and commas, but don't add /month during input
            // /month will be added only after saving
            const value = e.target.value;
            if (!value) {
              setFieldValue('price', '');
              return;
            }
            // Remove all non-numeric characters
            const numericValue = value.toString().replace(/[^0-9]/g, '');
            if (!numericValue) {
              setFieldValue('price', '');
              return;
            }
            // Convert to number and format with commas and $, but no /month
            const number = parseInt(numericValue, 10);
            const formatted = `$${number.toLocaleString('en-US')}`;
            setFieldValue('price', formatted);
          };

          // Handle HOA field formatting
          const handleHOAChange = (e) => {
            const rawValue = e.target.value;
            setFieldValue('hoa', rawValue);
          };

          const handleHOABlur = (e) => {
            // Format HOA with currency and commas, but don't add /month
            const value = e.target.value;
            if (!value) {
              setFieldValue('hoa', '');
              return;
            }
            // Remove all non-numeric characters
            const numericValue = value.toString().replace(/[^0-9]/g, '');
            if (!numericValue) {
              setFieldValue('hoa', '');
              return;
            }
            // Convert to number and format with commas and $, but no /month
            const number = parseInt(numericValue, 10);
            const formatted = `$${number.toLocaleString('en-US')}`;
            setFieldValue('hoa', formatted);
          };

          return (
            <Form>
              <DialogTitle sx={{ 
                bgcolor: '#1a1d29',
                color: '#ffffff',
                fontWeight: 500,
                fontSize: '1.125rem',
                pb: 2,
                pt: 2.5,
                px: 3,
              }}>
                {isEdit ? 'Edit' : 'Create'} {listingType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
              <Box>
                {isShortTerm ? (
                  <Grid container spacing={2} sx={{ pt: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.title && !!errors.title}
                        helperText={(touched.title && errors.title) || 'Optional: Property title (e.g., "Luxury Oceanfront Condo")'}
                        placeholder="Luxury Oceanfront Condo"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subtitle"
                        name="subtitle"
                        value={values.subtitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.subtitle && !!errors.subtitle}
                        helperText={(touched.subtitle && errors.subtitle) || 'Optional: Brief property description or subtitle'}
                        placeholder="Stunning views with modern amenities"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Rating"
                        name="rating"
                        type="number"
                        value={values.rating}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.rating && !!errors.rating}
                        helperText={(touched.rating && errors.rating) || 'Optional: Rating from 0 to 5 with up to 2 decimal places (e.g., 4.86)'}
                        inputProps={{ min: 0, max: 5, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Reviews Count"
                        name="reviewsCount"
                        type="number"
                        value={values.reviewsCount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.reviewsCount && !!errors.reviewsCount}
                        helperText={(touched.reviewsCount && errors.reviewsCount) || 'Optional: Total number of reviews (whole number)'}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Bedrooms"
                        name="bedrooms"
                        type="number"
                        value={values.bedrooms}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText="Optional: Number of bedrooms"
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Beds"
                        name="beds"
                        type="number"
                        value={values.beds}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.beds && !!errors.beds}
                        helperText={(touched.beds && errors.beds) || 'Optional: Number of beds (whole number)'}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Baths"
                        name="baths"
                        type="number"
                        value={values.baths}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.baths && !!errors.baths}
                        helperText={(touched.baths && errors.baths) || 'Optional: Number of bathrooms (can include decimals)'}
                        inputProps={{ min: 0, step: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Guests"
                        name="guests"
                        type="number"
                        value={values.guests}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.guests && !!errors.guests}
                        helperText={(touched.guests && errors.guests) || 'Optional: Maximum number of guests (whole number)'}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Location"
                        name="location"
                        value={values.location}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.location && !!errors.location}
                        helperText={(touched.location && errors.location) || 'Optional: Property location (e.g., "San Diego, CA")'}
                        placeholder="San Diego, CA"
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2} sx={{ pt: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Beds"
                        name="beds"
                        type="number"
                        value={values.beds}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.beds && !!errors.beds}
                        helperText={(touched.beds && errors.beds) || 'Optional: Number of bedrooms (whole number)'}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Baths"
                        name="baths"
                        type="number"
                        value={values.baths}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.baths && !!errors.baths}
                        helperText={(touched.baths && errors.baths) || 'Optional: Number of bathrooms (can include decimals, e.g., 2.5)'}
                        inputProps={{ min: 0, step: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Square Feet"
                        name="sqft"
                        type="number"
                        value={values.sqft}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.sqft && !!errors.sqft}
                        helperText={(touched.sqft && errors.sqft) || 'Optional: Total square footage (whole number, e.g., 1200)'}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Acres"
                        name="acres"
                        value={values.acres}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.acres && !!errors.acres}
                        helperText={(touched.acres && errors.acres) || 'Optional: Lot size in acres (e.g., "2.5" or "2")'}
                        placeholder="2.5"
                      />
                    </Grid>
                    {isPast && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="HOA Fee"
                          name="hoa"
                          value={values.hoa || ''}
                          onChange={handleHOAChange}
                          onBlur={handleHOABlur}
                          placeholder="$500"
                          helperText="Enter HOA fee (e.g., 500 or $500)"
                        />
                      </Grid>
                    )}
                  </Grid>
                )}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Price"
                      name="price"
                      value={values.price || ''}
                      onChange={handlePriceChange}
                      onBlur={handlePriceBlur}
                      error={touched.price && !!errors.price}
                      helperText={(touched.price && errors.price) || 'Optional: Enter price as number (e.g., 1700300) - will format automatically'}
                      placeholder="1700300"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address && !!errors.address}
                      helperText={(touched.address && errors.address) || 'Optional: Full street address (e.g., "123 Main St, San Francisco, CA 94118")'}
                      placeholder="123 Main St, San Francisco, CA 94118"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      select
                      label="Status"
                      name="status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.status && !!errors.status}
                      helperText={(touched.status && errors.status) || 'Optional: Select listing status'}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Sold">Sold</MenuItem>
                      <MenuItem value="Rented">Rented</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="URL"
                      name="url"
                      value={values.url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.url && !!errors.url}
                      helperText={(touched.url && errors.url) || 'Optional: Full URL to listing page (e.g., https://example.com/listing/123)'}
                      placeholder="https://example.com/listing/123"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      {isPast ? 'Image' : 'Images'}
                    </Typography>
                    <ImageUploader
                      value={isPast ? (values.image ? [values.image] : []) : values.images}
                      onChange={(newImages) => {
                        if (isPast) {
                          // Only keep the first image for past listings
                          const singleImage = newImages[0] || null;
                          setFieldValue('image', singleImage);
                          setFieldValue('images', singleImage ? [singleImage] : []);
                        } else {
                          setFieldValue('images', newImages);
                        }
                      }}
                      multiple={!isPast}
                      maxFiles={isPast ? 1 : undefined}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button 
                onClick={onClose} 
                variant="text" 
                sx={{ 
                  minWidth: 80,
                  textTransform: 'none',
                  color: '#6b7280',
                  fontWeight: 400,
                  '&:hover': {
                    bgcolor: '#f5f7fa',
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={isSubmitting}
                sx={{ 
                  minWidth: 80,
                  textTransform: 'none',
                  bgcolor: '#1a1d29',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: '#2d3142',
                  },
                  '&:disabled': {
                    bgcolor: '#9ca3af',
                  }
                }}
              >
                {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Listing'}
              </Button>
            </DialogActions>
          </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ListingForm;

